import {
	PresetData,
	ResolvedPresetsRefArray,
	ResolvedPresetsVariantRelation,
	ResolvedRefArray,
	ResolvedVariantRelation,
	UnresolvedRefArray,
	UnresolvedVariantRelation,
	VariantRelationOnlyRefArray,
} from '@wix/thunderbolt-becky-types'
import _ from 'lodash'
import { REF_DELIMITER, removeHash } from '@wix/thunderbolt-byref'
import {
	getDataOverrides,
	getOverridesRefArrayId,
	inflateResolvedRefArray,
	mergeRefArrayOverrides,
	resolveRefArray,
	InflationDataItem,
	InflationDataMap,
} from './inflationUtils'

// "ref1_r_ref2_r_compId-query => ["ref2_r_compId-query", "ref1_r_ref2_r_compId-query"]
export const getQueries = (dataQuery: string): Array<string> => {
	const res: Array<string> = []
	_(dataQuery)
		.split(REF_DELIMITER)
		.reverse()
		.forEach((refId, index) => {
			if (index > 0) {
				res.push(`${refId}${REF_DELIMITER}${res[index - 1]}`)
			} else {
				res.push(refId)
			}
		})
	res.shift()
	return res
}

// Returns the item under the preset condition.
// If there is no item under the preset condition, returns the first item which isn't not under any condition.
const getItemUnderPresetCondition = (
	presetCondition: string,
	refArrayValues: ResolvedRefArray<InflationDataItem>['values']
): InflationDataItem => {
	const getDefaultItem = () =>
		// There should always be a default value.
		// There might be more than one default item during preset resolving.
		_.findLast(refArrayValues, (value) => value.type !== 'VariantRelation') as InflationDataItem

	const variantRelationWithPresetCondition = refArrayValues.find(
		(value) =>
			value.type === 'VariantRelation' &&
			value.variants.some((variantId) => removeHash(variantId) === presetCondition)
	) as ResolvedVariantRelation<InflationDataItem>
	return variantRelationWithPresetCondition ? variantRelationWithPresetCondition.to : getDefaultItem()
}

const resolveItemsUnderPreset = (
	presetDataItem: PresetData | ResolvedPresetsVariantRelation,
	dataItemType: 'layout' | 'style' | 'variable',
	resolvedRefArrayValues: ResolvedRefArray<InflationDataItem>['values'],
	dataQuery: string,
	mergedDataMap: InflationDataMap
): ResolvedRefArray<InflationDataItem>['values'][0] => {
	const presetItemKey = dataItemType === 'style' ? 'style' : 'layout'

	if (presetDataItem.type === 'PresetData') {
		// Get the item under the specific preset and make in as default.
		const item = getItemUnderPresetCondition(presetDataItem[presetItemKey], resolvedRefArrayValues)
		const inflatedItemId = `${dataQuery}-${presetDataItem.id}-${dataItemType}`
		const itemOverride = mergedDataMap[inflatedItemId] as InflationDataItem
		return itemOverride
			? itemOverride
			: {
					...item,
					id: inflatedItemId,
			  }
	}

	const presetItem = presetDataItem.to
	const inflatedVariantRelationId = `${dataQuery}-${presetDataItem.id}`
	const item = getItemUnderPresetCondition(presetItem[presetItemKey], resolvedRefArrayValues)
	const inflatedItemId = `${dataQuery}-${presetDataItem.id}-${dataItemType}`
	const variantRelationOverride = mergedDataMap[inflatedVariantRelationId] as UnresolvedVariantRelation
	if (variantRelationOverride) {
		// If we have override for the variant relation we should use it and just inflate the IDs in order
		// to be able to use the same widget more than one time in the same page
		const itemOverride = mergedDataMap[removeHash(variantRelationOverride.to)] as InflationDataItem
		// We resolve the "to" in order to be consistent with the flow when there is no override
		return {
			...variantRelationOverride,
			to: itemOverride,
		}
	}

	// Get the item under the specific preset and take the condition from the presetData
	const itemOverride = mergedDataMap[inflatedItemId] as InflationDataItem
	return {
		...presetDataItem,
		id: inflatedVariantRelationId,
		to: itemOverride
			? itemOverride
			: {
					...item,
					id: inflatedItemId,
			  },
	}
}

export const inflateWithPresets = (
	resolvedRefArray: ResolvedRefArray<InflationDataItem>,
	dataQuery: string,
	presets: Array<ResolvedPresetsRefArray['values']>,
	mapName: 'layout_data' | 'theme_data',
	mergedDataMap: InflationDataMap
) => {
	const presetItemKey = mapName === 'layout_data' ? 'layout' : 'style'
	const queries = getQueries(dataQuery)

	const orderedPresets = _.reverse([...presets])

	orderedPresets.forEach((presetsArray, index) => {
		dataQuery = queries[index]
		if (presetsArray.length) {
			resolvedRefArray.values = presetsArray.map((presetDataItem) =>
				resolveItemsUnderPreset(
					presetDataItem,
					presetItemKey,
					resolvedRefArray.values,
					dataQuery,
					mergedDataMap
				)
			)
		}
		const overridesRefArray = mergedDataMap[getOverridesRefArrayId(dataQuery)] as UnresolvedRefArray
		if (overridesRefArray && overridesRefArray.type === 'RefArray') {
			const resolvedOverridesRefArray = resolveRefArray(overridesRefArray, mergedDataMap)
			const inflatedAndResolvedOverridesRefArray = inflateResolvedRefArray(
				resolvedOverridesRefArray,
				dataQuery,
				mergedDataMap
			) as VariantRelationOnlyRefArray<InflationDataItem>

			resolvedRefArray.values = mergeRefArrayOverrides(
				resolvedRefArray.values,
				inflatedAndResolvedOverridesRefArray.values.filter((item) => item.type === 'VariantRelation')
			)
		}
	})

	return getDataOverrides(resolvedRefArray, dataQuery)
}

/**
 * In cases where items are inflated by preset-override, they don't have the full inflated id.
 * E.g., the inflated component's Id is `pageRefCompId_r_widgetRefCompId_r_compId`,
 * but the style\layout item's id is `widgetRefCompId_r_compId-styleId-presetData123-style` (missing the page refComp id prefix)
 * The viewer doesn't mind this (and the id is still unique in the page), but this causes issues in DM,
 * because DM thinks those items are orphans, and it deletes them during save (aka styles GC).
 * So we need to make sure that the ids have the full compId prefix.
 * @param item
 * @param inflatedDataQuery
 * @returns
 */
const ensureFullInflatedOverrideDataQuery = <
	Item extends InflationDataItem | ResolvedVariantRelation<InflationDataItem>
>(
	item: Item,
	inflatedDataQuery: string
): Item => {
	if (item.id.startsWith(inflatedDataQuery)) {
		return item
	}
	const styleIdOrLayoutQuery = _.last(inflatedDataQuery.split('-')) as 'styleId' | 'layoutQuery'
	const itemSuffix = item.id.split(styleIdOrLayoutQuery)[1] // e.g. -presetData123-style

	const id = `${inflatedDataQuery}${itemSuffix}`

	return {
		...item,
		id,
		...(item.type === 'VariantRelation' && {
			to: ensureFullInflatedOverrideDataQuery(item.to, inflatedDataQuery),
		}),
	}
}

const filterUnresolvedValues = (
	refArrayValues: ResolvedRefArray<InflationDataItem>['values'],
	pageVariants: Record<string, true>,
	inflatedDataQuery: string
) => {
	// Filter variantRelations with unresolved condition (condition from remote or other blocks pages)
	const resolvedVariantRelations = refArrayValues.filter(
		(value) =>
			value.type === 'VariantRelation' && value.variants.every((variantId) => pageVariants[removeHash(variantId)])
	)

	// Filter variant relations with the same variants array (taking the first, which must be the result of preset override because of the order of the preset resolving which is inside out)
	const uniqueVariantRelations = _.uniqWith(
		resolvedVariantRelations,
		(x, y) =>
			x.type === 'VariantRelation' &&
			y.type === 'VariantRelation' &&
			_.isEqual(x.variants.sort(), y.variants.sort())
	)

	// At this point we have 1+ default items in refArrayValues
	// The first one is the default item in the original style\layout refArray, we should filter it out.
	// If we have preset override that was made in desktop, we'll have a default style\layout items somewhere in the middle.
	// We can have 1 default style\layout item that generated from presetOverride for each level of widget.
	// If we are in the consumer (EditorX) then the last default item is the one that comes from the preset selected in desktop.
	// The order of priority is: inner presetOverride > outer presetOverride > desktop

	const numberOfDefaultValues = refArrayValues.filter((val) => val.type !== 'VariantRelation').length
	if (numberOfDefaultValues > 1) {
		const firstDefaultItemIndex = refArrayValues.findIndex((val) => val.type !== 'VariantRelation')
		refArrayValues.splice(firstDefaultItemIndex, 1)
	}
	const lastDefaultItem = _.find(refArrayValues, (val) => val.type !== 'VariantRelation') as InflationDataItem

	const refArray = lastDefaultItem ? [lastDefaultItem, ...uniqueVariantRelations] : uniqueVariantRelations

	return refArray.map((item) => ensureFullInflatedOverrideDataQuery(item, inflatedDataQuery))
}

export const inflateRefArrayWithPresets = (
	resolvedTemplateRefArray: ResolvedRefArray<InflationDataItem>,
	inflatedDataQuery: string,
	refCompPresets: Array<ResolvedPresetsRefArray['values']>,
	resolvedMergedDataMap: InflationDataMap,
	dataItemType: 'layout' | 'style' | 'variable',
	pageVariants: Record<string, true>
) => {
	const queries = getQueries(inflatedDataQuery)

	const resolvedRefArrayValuesWithPresetsApplied = refCompPresets.reduce(
		(previousResolvedRefArrayValues, presetsArray, index) => {
			const dataQuery = queries[index]
			// Can be smarter than just concat. Currently we filter unneeded values using filterUnresolvedValues
			const resolveRefArrayValuesWithPresetsApplied = previousResolvedRefArrayValues.concat(
				presetsArray.map((presetDataItem) =>
					resolveItemsUnderPreset(
						presetDataItem,
						dataItemType,
						previousResolvedRefArrayValues,
						dataQuery,
						resolvedMergedDataMap
					)
				)
			)

			const resolvedOverridesRefArray = resolvedMergedDataMap[dataQuery] as ResolvedRefArray<InflationDataItem>
			if (!resolvedOverridesRefArray || resolvedOverridesRefArray.type !== 'RefArray') {
				return resolveRefArrayValuesWithPresetsApplied
			}

			const inflatedAndResolvedOverridesRefArray = inflateResolvedRefArray(
				resolvedOverridesRefArray,
				dataQuery,
				resolvedMergedDataMap
			) as VariantRelationOnlyRefArray<InflationDataItem>

			const resolvedValuesWithPresetsAndOverridesApplied = mergeRefArrayOverrides(
				resolveRefArrayValuesWithPresetsApplied,
				inflatedAndResolvedOverridesRefArray.values.filter((item) => item.type === 'VariantRelation')
			)

			return resolvedValuesWithPresetsAndOverridesApplied
		},
		resolvedTemplateRefArray.values
	)

	return getDataOverrides(
		{
			...resolvedTemplateRefArray,
			values: filterUnresolvedValues(resolvedRefArrayValuesWithPresetsApplied, pageVariants, inflatedDataQuery),
		},
		inflatedDataQuery
	)
}
